/**
 * Quiz Recommendation Logic
 * assets/quiz-logic.js
 *
 * This file contains the algorithm to match quiz responses with products
 */

const QuizLogic = {
  /**
   * Main function to get recommended products based on quiz responses
   * @param {Array} products - All available products
   * @param {Object} responses - User's quiz responses
   * @returns {Array} - Filtered and sorted recommended products
   */
  getRecommendedProducts(products, responses) {
    console.log('Calculating recommendations...', { products, responses });

    // Filter products based on responses
    let filteredProducts = this.filterProducts(products, responses);

    // Score products based on match quality
    let scoredProducts = this.scoreProducts(filteredProducts, responses);

    // Sort by score (highest first)
    scoredProducts.sort((a, b) => b.score - a.score);

    // Group by category and select best from each
    let recommendations = this.selectByCategory(scoredProducts, responses);

    // Limit based on regimen preference
    recommendations = this.limitByRegimen(recommendations, responses);

    console.log('Final recommendations:', recommendations);
    return recommendations.map((item) => item.product);
  },

  /**
   * Filter products that match basic criteria
   */
  filterProducts(products, responses) {
    return products.filter((product) => {
      // Check if product is available
      if (!product.available) return false;

      // Check skin type compatibility
      if (!this.matchesSkinType(product, responses['skin-type'])) {
        return false;
      }

      // Check age group compatibility
      if (responses['age-group'] && responses['age-group'] !== 'prefer_not_to_say') {
        if (!this.matchesAgeGroup(product, responses['age-group'])) {
          return false;
        }
      }

      return true;
    });
  },

  /**
   * Check if product matches skin type
   */
  matchesSkinType(product, skinType) {
    if (!skinType) return true;

    // Check metafield
    const productSkinTypes = product.metafields?.quiz?.skin_types || [];

    // If no metafields, check tags
    if (productSkinTypes.length === 0) {
      const tags = product.tags || [];

      // Map quiz values to tag values
      const skinTypeTagMap = {
        oily_all_day: ['oily', 'oil-control'],
        oily_noon: ['combination', 'oily'],
        normal: ['all-skin-types', 'normal'],
        dry_tight: ['dry', 'hydrating'],
        combination: ['combination', 'all-skin-types'],
        sensitive: ['sensitive', 'gentle'],
      };

      const matchTags = skinTypeTagMap[skinType] || [];
      return matchTags.some((tag) => tags.includes(tag));
    }

    return productSkinTypes.includes(skinType) || productSkinTypes.includes('all');
  },

  /**
   * Check if product matches age group
   */
  matchesAgeGroup(product, ageGroup) {
    if (!ageGroup || ageGroup === 'prefer_not_to_say') return true;

    const productAgeGroups = product.metafields?.quiz?.age_groups || [];

    if (productAgeGroups.length === 0) return true; // If not specified, include all

    return productAgeGroups.includes(ageGroup) || productAgeGroups.includes('all');
  },

  /**
   * Score products based on how well they match skincare goals
   */
  scoreProducts(products, responses) {
    const skincareGoals = responses['skincare-goals'] || [];

    return products.map((product) => {
      let score = 0;

      // Base score
      score += 10;

      // Score for matching skincare goals
      const productGoals = product.metafields?.quiz?.skincare_goals || [];

      if (productGoals.length > 0) {
        skincareGoals.forEach((goal) => {
          if (productGoals.includes(goal)) {
            score += 20; // High weight for goal match
          }
        });
      } else {
        // Fallback to tags
        const tags = product.tags || [];
        skincareGoals.forEach((goal) => {
          if (tags.includes(goal)) {
            score += 15;
          }
        });
      }

      // Priority from metafield (lower number = higher priority)
      const priority = product.metafields?.quiz?.priority || 5;
      score += (10 - priority) * 5;

      // Boost newer products slightly
      if (product.tags?.includes('new')) {
        score += 5;
      }

      // Boost products with good ratings
      // (You would fetch this from product metafields if available)
      score += Math.random() * 10; // Placeholder

      return {
        product: product,
        score: score,
      };
    });
  },

  /**
   * Select products by category to ensure variety
   */
  selectByCategory(scoredProducts, responses) {
    const categories = ['cleanser', 'serum', 'am_moisturizer', 'pm_moisturizer', 'eye', 'treatment'];
    const selected = [];
    const categoryCounts = {};

    // Get regimen preference to know how many products to recommend
    const regimenPref = responses['regimen-preference'] || 'complete';
    const maxProducts = this.getMaxProducts(regimenPref);

    // First pass: get one product from each essential category
    const essentialCategories = ['cleanser', 'serum', 'am_moisturizer'];

    essentialCategories.forEach((category) => {
      const productsInCategory = scoredProducts.filter((item) => this.getProductCategory(item.product) === category);

      if (productsInCategory.length > 0) {
        selected.push(productsInCategory[0]);
        categoryCounts[category] = 1;
      }
    });

    // Second pass: fill remaining slots with highest scored products
    scoredProducts.forEach((item) => {
      if (selected.length >= maxProducts) return;
      if (selected.some((s) => s.product.id === item.product.id)) return;

      const category = this.getProductCategory(item.product);
      const countInCategory = categoryCounts[category] || 0;

      // Limit products per category (max 2 of same type)
      if (countInCategory < 2) {
        selected.push(item);
        categoryCounts[category] = countInCategory + 1;
      }
    });

    return selected;
  },

  /**
   * Get product category from metafield or tags
   */
  getProductCategory(product) {
    // Try metafield first
    if (product.metafields?.quiz?.product_category) {
      return product.metafields.quiz.product_category;
    }

    // Fallback to tags
    const tags = product.tags || [];
    const categories = ['cleanser', 'serum', 'am_moisturizer', 'pm_moisturizer', 'eye', 'treatment'];

    for (const category of categories) {
      if (tags.includes(category) || tags.includes(category.replace('_', '-'))) {
        return category;
      }
    }

    return 'other';
  },

  /**
   * Get maximum number of products based on regimen preference
   */
  getMaxProducts(regimenPref) {
    const limits = {
      basic: 3,
      complete: 5,
      ultimate: 7,
    };
    return limits[regimenPref] || 5;
  },

  /**
   * Limit recommendations based on regimen preference
   */
  limitByRegimen(recommendations, responses) {
    const regimenPref = responses['regimen-preference'] || 'complete';
    const maxProducts = this.getMaxProducts(regimenPref);

    return recommendations.slice(0, maxProducts);
  },
};

// Make available globally
window.QuizLogic = QuizLogic;
